import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { apiHolder, apis, getIn18Text, inWindow, isElectron, } from 'api';
import { Badge, Popover, Tooltip } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { navigate } from 'gatsby';
import debounce from 'lodash/debounce';
import { DiskTipKeyEnum } from '@web-disk/disk';
import Avatar from '@web-common/components/UI/Avatar/avatar';
import SiriusMessage from '@web-common/components/UI/Message/SiriusMessage';
import { UsageGuide } from '@web-common/components/UsageGuide';
import { DiskActions, useActions, useAppDispatch, useAppSelector } from '@web-common/state/createStore';
import { ConfigActions, ExpandMenuActions, ReadCountActions } from '@web-common/state/reducer';
import { AvatarList } from '@web-common/components/UI/AvatarList/avatar-list';
import { ELECTRON_TITLE_FIX_HEIGHT } from '@web-common/utils/constant';
import { shallowEqual } from 'react-redux';
import { getIsFreeVersionUser, getIsSomeMenuVisbleSelector } from '@web-common/state/reducer/privilegeReducer';
import { registerRouterInterceptorDesktopV2 } from 'env_def';
import { ReactComponent as Noti } from '@web-common/images/icons/noti.svg';
import { ReactComponent as Kf } from '@web-common/images/icons/kf.svg';
import { ReactComponent as More } from '@web-common/images/icons/more.svg';
import { RewardAlreadyGotit } from '@web-common/components/FloatToolButton/const';
import { isMatchCustomerManageRoute, isMatchCustomerPerformanceRoute } from '@web-common/hooks/use-l2c-crm-menu-data';
import NoticeCard from '@web-notify/components/NoticeCard';
import { useConnectNotifySSE } from '@web-notify/hooks/useConnectNotifySSE';
import { doFetchNoticeNum } from '@web-common/state/reducer/noticeCenterReducer';
import VideoDrawer from '@web-common/components/UI/VideoDrawer';
import MoreMenu from '@web-common/components/MoreMenu';
import { bus } from '@web-common/utils/bus';
import { WmKfEntry } from '@web-common/components/WmKfEntry';
import { getDefaultOrderFromLocal, getKeyBoardList, navigateToSchedule, recordDragList, saveTabsToLocal, sortByOrder, TAB_STR_SEP } from './util';
import SideBarTabItem from '@/layouts/Main/sideBarTabItem';
import { ReactComponent as MenuFoldIcon } from '@/images/icons/edm/menu-fold.svg';
import { ReactComponent as MenuExpandIcon } from '@/images/icons/edm/menu-expand.svg';
import { ReactComponent as MenuDownLoadIcon } from '@/images/icons/edm/menu-download.svg';
import { ReactComponent as Setting } from '@/images/icons/sidebar/setting.svg';
import style from './main.module.scss';
import UpdateApp from './updateApp';
import { useTabs } from './tabsToDisplay';
import { WAIMAO_PRODUCT_VERSIONS } from '@web-common/conf/waimao/constant';
import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
const systemApi = apiHolder.api.getSystemApi();
const mailApi = apiHolder.api.requireLogicalApi(apis.mailApiImpl);
const eventApi = apiHolder.api.getEventApi();
const diskApi = apiHolder.api.requireLogicalApi(apis.netStorageImpl);
const performanceApi = apiHolder.api.requireLogicalApi(apis.performanceImpl);
const trackApi = apiHolder.api.requireLogicalApi(apis.dataTrackerApiImp);
const inElectron = systemApi.isElectron;
const isMac = inElectron() ? window.electronLib.env.isMac : apiHolder.env.isMac;
const inEdm = process.env.BUILD_ISEDM;
const isClassicEdmWeb = !isElectron() && inEdm;
const trackEventMap = {
    worktable: 'client_1_home_page',
    mailbox: 'client_1_email_management',
    wa: 'client_1_personal_WA_enter_point_click',
    wmData: 'client_1_big_data_on_foreign_trade',
    edm: 'client_1_smart_marketing',
    customerManage: 'client_1_customer_management',
    customerPerformance: 'client_1_customer_performance',
    coop: 'client_1_coordinate_office',
    site: 'client_1_brand_build',
    enterpriseSetting: 'client_1_enterprise_setup',
};
const UN_DRAGGABLE_INDEX = 1000;
const SideTabBar = ({ onChange, activeKey = '', tabs = [], handleJumpOutClick, handleSiteManageClick }) => {
    const tabsNameStr = tabs.map(v => v.name).join(TAB_STR_SEP);
    const dispatch = useAppDispatch();
    const unReadCountActions = useActions(ReadCountActions);
    const unreadCount = useAppSelector(state => state.readCountReducer.unreadCount, shallowEqual);
    const isNewUser = useAppSelector(state => state.diskReducer.isNewUser);
    const guideTipsInfo = useAppSelector(state => state.diskReducer.guideTipsInfo, shallowEqual);
    const isFoldMenu = useAppSelector(state => state.expandMenuReducer.isFold);
    const version = useAppSelector(state => state.privilegeReducer.version);
    const visibleEnterpriseSetting = useAppSelector(state => getIsSomeMenuVisbleSelector(state.privilegeReducer, ['ORG_SETTINGS']));
    const { sharedAccount } = useAppSelector(state => state.loginReducer);
    const { localList, mobileList } = useAppSelector(state => state.loginReducer.accountList);
    const isFreeVersionUser = useAppSelector(state => getIsFreeVersionUser(state.privilegeReducer));
    const navigateVersion = useAppSelector(state => state.privilegeReducer.navigateVersion);
    const coop = useMemo(() => tabs.filter(tab => ['schedule', 'disk', 'contact', 'apps'].includes(tab.name)), [tabsNameStr]);
    const coopStr = coop.map(v => v.name).join('');
    const shareAccountNum = useMemo(() => {
        const isLoginCount = sharedAccount.isSharedAccountLogin ? 1 : 0;
        const sharedAccounts = sharedAccount.sharedAccounts ? sharedAccount.sharedAccounts.filter(s => !s.isCurrentAccount).length : 0;
        return isLoginCount + sharedAccounts;
    }, [sharedAccount.isSharedAccountLogin, sharedAccount.sharedAccounts]);
    const accountCount = localList.filter(i => !i.expired).length + mobileList.length;
    const [block, setBlock] = useState(false);
    const [keyBoardList, setKeyBoardList] = useState([]);
    const [shouldUpdateApp, setShouldUpdateApp] = useState(false);
    const [tabsWithOrder, setTabsWithOrder] = useState(() => getDefaultOrderFromLocal(tabs));
    const tabsWithOrderNameStr = tabsWithOrder.map(v => v.name).join('');
    const tabBarRef = useRef(null);
    const isDragging = useRef(false);
    const [isEleInMoreTabDragging, setIsEleInMoreTabDragging] = useState(false);
    const { tabToDisplay, tabInMore } = useTabs(tabsWithOrder, tabBarRef, shareAccountNum + accountCount);
    const tabToDisplayStr = useMemo(() => tabToDisplay.map(v => v.name).join(''), [tabToDisplay]);
    const tabImMoreStr = tabInMore.map(v => v.name).join('');
    const { doConnectSSE } = useConnectNotifySSE();
    const unreadNoticeNum = useAppSelector(state => state.noticeCenterReducer.noticeNum.unreadNum);
    const showBlockingMsg = useCallback(() => {
        SiriusMessage.warn({ content: getIn18Text('QINGQIEHUANZHIQI') }).then();
    }, []);
    const doUpdateMailBoxStat = useCallback(debounce(() => mailApi.doUpdateMailBoxStat(), 500, { maxWait: 2000 }), []);
    const setBlockFn = useCallback((blk, page) => {
        if (page) {
            console.time('sidebar click ' + page);
            performanceApi.time({
                statKey: 'side_bar_click_time' + (inEdm ? '_inEdm' : ''),
                statSubKey: page,
            });
        }
        if (blk) {
            showBlockingMsg();
            trackApi.track('pc_leftNavigationBarTab', { tabName: page, operate: 'blocking' });
            console.timeEnd('sidebar click ' + page);
            return blk;
        }
        setTimeout(() => {
            if (page === 'mailbox') {
                doUpdateMailBoxStat();
            }
        }, 0);
        if (page === 'schedule' && navigateToSchedule()) {
            return blk;
        }
        if (onChange && page) {
            onChange(page);
        }
        trackApi.track('pc_leftNavigationBarTab', { tabName: page, operate: 'click' });
        return blk;
    }, []);
    const handleClick = useCallback((page, tab) => () => {
        setTimeout(() => {
            dispatch(ConfigActions.closeVideoDrawer());
        }, 50);
        if (page && page.startsWith('jumpOut_') && handleJumpOutClick) {
            const jumpOutTarget = page.split('jumpOut_');
            handleJumpOutClick(jumpOutTarget[1] || '');
            return;
        }
        if (page === 'siteManage') {
            handleSiteManageClick && tab && handleSiteManageClick(tab);
            return;
        }
        if (process.env.BUILD_ISEDM) {
            let trackName = page || '';
            if (isMatchCustomerManageRoute(page)) {
                trackName = 'customerManage';
            }
            if (isMatchCustomerPerformanceRoute(page)) {
                trackName = 'customerPerformance';
            }
            const trackEventId = trackEventMap[trackName];
            if (trackEventId) {
                trackApi.track(trackEventId, { version: 1 });
            }
        }
        setBlock(blk => setBlockFn(blk, page));
    }, []);
    const handleDoubleClick = useCallback((page, focus) => () => {
        if (page !== 'message' || !focus) {
            return;
        }
        eventApi.sendSysEvent({
            eventName: 'messageDoubleClick',
        });
    }, []);
    const checkIsNewUser = useCallback(() => {
        diskApi.getNewUserAnnouncement().then(res => {
            var _a;
            dispatch(DiskActions.setIsNewUser(!!res));
            if (res) {
                const announcementId = res.id;
                let firstDocUrl = '';
                try {
                    firstDocUrl = (_a = JSON.parse(res.content)) === null || _a === void 0 ? void 0 : _a.firstDocUrl;
                }
                catch (e) {
                    console.error('checkIsNewUser error', e);
                }
                dispatch(DiskActions.setIsNewUser(!!res));
                dispatch(DiskActions.setGuideTipsInfoByKey({
                    key: DiskTipKeyEnum.WELCOME_TIP,
                    value: Object.assign(Object.assign({}, guideTipsInfo[DiskTipKeyEnum.WELCOME_TIP]), { content: {
                            announcementId,
                            firstDocUrl,
                        } }),
                }));
            }
        });
    }, []);
    const getUnreadNum = useCallback((tabName, _unreadCount) => {
        if (tabName === 'coop') {
            return _unreadCount.message;
        }
        return _unreadCount[tabName];
    }, []);
    const renderTabItem = useCallback((tabProps) => {
        const { tab, _unreadCount, _activeKey, _isNewUser, _keyboardList, tabListType, index, isDragDisabled = false } = tabProps;
        const inMoreTab = tabListType === 'more';
        const { name, tag, hidden, redPoint } = tab;
        const focus = _activeKey === tab.name;
        const unread = getUnreadNum(name, _unreadCount);
        const isShowBetaTag = name === 'disk';
        const showTag = !inMoreTab && name === 'jumpOut_acquisition';
        const iconTagText = name === 'jumpOut_acquisition' ? getIn18Text('freeTrail') : '';
        const jumpOutTagStyle = {
            height: '22px',
            lineHeight: '22px',
            left: '-8px',
            top: '-10px',
            padding: '0px 2px',
            transform: 'scale(0.7)',
        };
        const iconTagStyle = name === 'jumpOut_acquisition' ? jumpOutTagStyle : {};
        const keyboardItem = _keyboardList.find(item => item.id === tab.name);
        const title = tag + ((keyboardItem === null || keyboardItem === void 0 ? void 0 : keyboardItem.show) ? ' (' + (keyboardItem === null || keyboardItem === void 0 ? void 0 : keyboardItem.key) + ')' : '');
        const realTitle = (React.createElement(React.Fragment, null,
            React.createElement("div", { className: style.sdiBarTipArrow }),
            title));
        return (React.createElement(Draggable, { draggableId: name, index: index, key: name, isDragDisabled: isDragDisabled }, provided => (React.createElement("div", Object.assign({ className: style.sideBarItemContainer, ref: provided.innerRef }, provided.dragHandleProps, provided.draggableProps, { style: Object.assign({}, provided.draggableProps.style) }),
            React.createElement(SideBarTabItem, { tab: tab, title: realTitle, redPoint: redPoint, className: classnames([focus ? style.sideBarTabFocus : '', hidden ? style.sideBarTabHide : '']), active: focus, onClick: handleClick(name, tab), onDoubleClick: handleDoubleClick(name, focus), unread: unread, showNewTag: showTag, showBetaTag: isShowBetaTag, iconTagText: iconTagText, iconTagStyle: iconTagStyle })))));
    }, []);
    const TabToDisplayMemo = useMemo(() => {
        const tabList = tabToDisplay.map((tab, index) => renderTabItem({
            index,
            tab,
            tabListType: 'display',
            _unreadCount: unreadCount,
            _activeKey: activeKey,
            _keyboardList: keyBoardList,
            _isNewUser: isNewUser,
        }));
        return React.createElement("div", null, tabList);
    }, [tabToDisplayStr, unreadCount, activeKey, keyBoardList.length, isNewUser]);
    const AvatarCompMemo = useMemo(() => {
        if (inElectron() || inEdm) {
            return React.createElement(Avatar, { notifyBlocking: setBlock, activeKey: activeKey });
        }
        return null;
    }, [activeKey]);
    const AvatarListCompMemo = useMemo(() => {
        if (inElectron()) {
            return React.createElement(AvatarList, { isBlock: block, onBlock: showBlockingMsg });
        }
        return null;
    }, [block]);
    const UpdateAppCompMemo = useMemo(() => (shouldUpdateApp ? React.createElement(UpdateApp, null) : null), [shouldUpdateApp]);
    const DownloadIconCompMemo = useMemo(() => {
        if (isClassicEdmWeb) {
            const handleDownLoadClientClick = () => {
                window.location.href = 'https://sirius-config.qiye.163.com/api/pub/client/waimao/download';
            };
            const TitleContent = (React.createElement(React.Fragment, null,
                React.createElement("div", { className: style.sdiBarTipArrow }),
                getIn18Text('XIAZAIKEHUDUAN')));
            return (React.createElement(Tooltip, { placement: "right", trigger: "hover", title: TitleContent, overlayClassName: classnames(style.sideBarTipWrap) },
                React.createElement("div", { className: style.downloadMenuIcon, onClick: handleDownLoadClientClick },
                    React.createElement(MenuDownLoadIcon, null))));
        }
        return null;
    }, []);
    const CoopCompMemo = useMemo(() => {
        if (inEdm && version !== WAIMAO_PRODUCT_VERSIONS.WEBSITE) {
            const tabItems = coop.map(tab => renderTabItem({
                index: 100,
                tab,
                tabListType: 'coop',
                _unreadCount: unreadCount,
                _activeKey: activeKey,
                _keyboardList: keyBoardList,
                isDragDisabled: true,
            }));
            const content = React.createElement("div", { className: style.sideBarDropdown }, tabItems);
            return (React.createElement(Draggable, { draggableId: "tabCoop", index: UN_DRAGGABLE_INDEX, isDragDisabled: true }, provided => (React.createElement(Popover, { placement: "rightTop", trigger: "click", getPopupContainer: () => document.getElementById('sirius-side-bar-root'), content: content },
                React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.dragHandleProps, provided.draggableProps, { className: classnames(style.sideBarTab, style.coop, {
                        [style.siriusIconCoopEnhance]: ['schedule', 'disk', 'contact', 'apps'].includes(activeKey),
                    }) }),
                    React.createElement(React.Fragment, null,
                        React.createElement("div", { className: classnames(style.siriusIconCoop, style.icon, 'sirius-icon-coop') }),
                        React.createElement("div", { className: classnames(style.text, 'sirius-icon-coop') }, getIn18Text('XIETONGBANGONG'))))))));
        }
        return null;
    }, [coopStr, unreadCount, keyBoardList.length, version, activeKey]);
    const TabInMoreCompMemo = useMemo(() => {
        if (tabInMore.length > 0) {
            const isActive = tabInMore.map(m => m.name).includes(activeKey);
            const tabItems = tabInMore.map((tab, index) => renderTabItem({
                index: tabToDisplay.length + index,
                tab,
                tabListType: 'more',
                _unreadCount: unreadCount,
                _activeKey: activeKey,
                _keyboardList: keyBoardList,
            }));
            const padding = process.env.BUILD_ISEDM ? '8px 0' : 'unset';
            const itemHeight = process.env.BUILD_ISEDM ? 58 : 64;
            const height = process.env.BUILD_ISEDM ? tabInMore.length * itemHeight + 20 : tabInMore.length * itemHeight;
            const content = (React.createElement(Droppable, { droppableId: "moreTab" }, provided => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps, { className: process.env.BUILD_ISEDM ? style.sideBarDropdownEdm : style.sideBarDropdown, style: { height, padding } }), tabItems))));
            return (React.createElement(Draggable, { draggableId: "tabMore", index: UN_DRAGGABLE_INDEX, isDragDisabled: true }, provided => (React.createElement(Popover, { placement: "rightTop", trigger: "click", getPopupContainer: () => document.getElementById('sirius-side-bar-root'), content: content, overlayClassName: process.env.BUILD_ISEDM ? style.sideBarDropdownWrapperEdm : style.sideBarDropdownWrapper },
                React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.dragHandleProps, provided.draggableProps, { style: Object.assign(Object.assign({}, provided.draggableProps.style), { display: isEleInMoreTabDragging ? 'none' : 'flex' }), className: classnames([
                        style.sideBarTab,
                        process.env.BUILD_ISEDM ? style.sideBarTabMoreEdm : style.sideBarTabMore,
                        {
                            [style.sideBarTabMoreEnhance]: isActive,
                        },
                        isDragging.current ? style.isDragging : '',
                    ]) }),
                    React.createElement("div", { className: style.iconWrapper },
                        React.createElement("span", { className: style.sideBarMoreIcon })),
                    React.createElement("div", { className: style.sideBarTabLabel }, getIn18Text('GENGDUO')))))));
        }
        return null;
    }, [tabImMoreStr, tabToDisplayStr, unreadCount, keyBoardList.length, isDragging.current, isEleInMoreTabDragging, activeKey]);
    const [showTip, setShowTip] = useLocalStorageState(RewardAlreadyGotit, { defaultValue: false });
    const listener = () => {
        setShowTip(true);
    };
    useEffect(() => {
        bus.on('closeReward', listener);
        bus.on('closeTask', listener);
        return () => {
            bus.off('closeReward', listener);
            bus.off('closeTask', listener);
        };
    }, []);
    const onVisibleChange = useCreateCallbackForEvent((visible, type) => {
        if (visible) {
            if (type === 'more') {
                trackApi.track('waimao_more', { action: 'show' });
            }
            else if (type === 'notice') {
                trackApi.track('waimao_notification', { action: 'click' });
            }
        }
    });
    const MoreToolsTitle = useMemo(() => (React.createElement("div", { className: style.tip },
        React.createElement("span", { style: {
                display: 'block',
            } }, getIn18Text('KEDIANJICICHUJIXU')),
        React.createElement("div", { style: {
                textAlign: 'right',
                marginTop: '12px',
            } },
            React.createElement("span", { className: style.tipOk, onClick: e => {
                    setShowTip(false);
                    e.stopPropagation();
                } }, getIn18Text('ZHIDAOLE'))))), []);
    const tools = useMemo(() => isFreeVersionUser || navigateVersion
        ? []
        : [
            {
                key: 'more',
                available: version !== 'WEBSITE',
                component: (React.createElement(Popover, { placement: "rightBottom", trigger: "click", content: React.createElement(MoreMenu, null), onVisibleChange: visible => onVisibleChange(visible, 'more') },
                    React.createElement(Tooltip, { visible: showTip, placement: "right", overlayClassName: style.more, trigger: "hover", title: MoreToolsTitle },
                        React.createElement("div", { className: style.toolsIcon },
                            React.createElement(More, null))))),
            },
            {
                key: 'kfOnline',
                available: version !== 'WEBSITE',
                component: (React.createElement(WmKfEntry, { className: style.toolsIcon },
                    React.createElement(Kf, null))),
            },
            {
                key: 'notice',
                available: version !== 'WEBSITE',
                component: (React.createElement(Popover, { style: {
                        transform: 'translateX(22px)',
                        width: '329px',
                    }, placement: "rightBottom", overlayClassName: style.overlay, trigger: "click", onVisibleChange: visible => {
                        onVisibleChange(visible, 'notice');
                    }, destroyTooltipOnHide: true, content: React.createElement(NoticeCard, { showHeader: true, showWxStatus: true, width: 308, height: 560, onClose: () => onVisibleChange(false, 'notice'), source: "popup" }) },
                    React.createElement("div", { className: classnames(style.toolsIcon) },
                        React.createElement(Badge, { count: unreadNoticeNum, overflowCount: 99, size: "small", offset: [6, 0] },
                            React.createElement(Noti, null))))),
            },
        ], [isFreeVersionUser, navigateVersion, showTip, version, unreadNoticeNum]);
    const EnterpriseSettingMemo = useMemo(() => {
        if (inEdm) {
            if (visibleEnterpriseSetting && version !== 'WEBSITE') {
                return (React.createElement("div", { className: style.expandMenuIcon, onClick: () => {
                        if (process.env.BUILD_ISEDM) {
                            trackApi.track('client_1_enterprise_setup', { version: 1 });
                        }
                        navigate('#enterpriseSetting');
                    } },
                    React.createElement(Setting, null)));
            }
            return (React.createElement("div", { className: style.expandMenuIcon, onClick: () => dispatch(ExpandMenuActions.setIsFold(!isFoldMenu)) }, isFoldMenu ? React.createElement(MenuFoldIcon, null) : React.createElement(MenuExpandIcon, null)));
        }
        return null;
    }, [visibleEnterpriseSetting, isFoldMenu, version]);
    const ToolSetMemo = useMemo(() => {
        if (process.env.BUILD_ISEDM) {
            return (React.createElement("div", { className: classnames([style.toolSets]) },
                React.createElement(UsageGuide, { className: classnames('sirius-no-drag', style.newUsageGuide), placement: "rightBottom", trigger: "click" }),
                tools
                    .filter(t => t.available)
                    .map(i => (React.createElement(React.Fragment, { key: i.key }, i.component)))));
        }
        return null;
    }, [tools]);
    useEffect(() => {
        const orderNames = tabsWithOrder.map(i => i.name);
        const sortedTabs = sortByOrder(tabs, orderNames);
        let needReorder = false;
        if (sortedTabs.length !== orderNames.length) {
            needReorder = true;
        }
        else {
            const isDff = sortedTabs.some((tab, index) => {
                const item = tabsWithOrder[index];
                return tab.name !== item.name || tab.tag !== item.tag || !!tab.hidden !== !!item.hidden;
            });
            if (isDff) {
                needReorder = true;
            }
        }
        if (needReorder) {
            setTabsWithOrder(sortedTabs);
        }
    }, [tabs]);
    useEffect(() => {
        let lsId = 0;
        if (!inEdm) {
            lsId = eventApi.registerSysEventObserver('notificationChange', {
                func: (ev) => {
                    if (ev.eventStrData === 'mail') {
                        unReadCountActions.updateMailboxUnreadCount(ev.eventData);
                    }
                    if (ev.eventStrData === 'im') {
                        unReadCountActions.updateIMUnreadCount(ev.eventData);
                    }
                },
            });
        }
        if (systemApi.getCurrentUser()) {
            checkIsNewUser();
        }
        if (inWindow()) {
            setKeyBoardList(getKeyBoardList());
        }
        return () => {
            if (lsId) {
                eventApi.unregisterSysEventObserver('notificationChange', lsId);
            }
        };
    }, []);
    useEffect(() => {
        if (inEdm) {
            registerRouterInterceptorDesktopV2();
        }
        return () => {
        };
    }, []);
    useEffect(() => {
        let eventId;
        const EVENT_NAME = 'shouldUpdateAppChanged';
        if (window && window.electronLib) {
            eventId = eventApi.registerSysEventObserver(EVENT_NAME, {
                func: ev => {
                    setShouldUpdateApp(!!ev.eventData);
                },
            });
        }
        return () => {
            if (eventId) {
                eventApi.unregisterSysEventObserver(EVENT_NAME, eventId);
            }
        };
    }, []);
    useEffect(() => {
        const timer = inEdm
            ? window.setTimeout(() => {
                dispatch(doFetchNoticeNum());
                doConnectSSE();
            }, 2000)
            : 0;
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, []);
    const onDragEnd = useCallback((result) => {
        isDragging.current = false;
        setIsEleInMoreTabDragging(false);
        const { source, destination } = result;
        if (!destination) {
            return;
        }
        const fromIndex = source.index;
        const toIndex = destination.index;
        if (fromIndex === toIndex) {
            return;
        }
        const newList = recordDragList(tabsWithOrder, fromIndex, toIndex);
        setTabsWithOrder(newList);
        saveTabsToLocal(newList.map(v => v.name));
        trackApi.track('pc_marketing_workbench_left_bar', newList.filter(tab => !tab.hidden));
    }, [tabsWithOrderNameStr]);
    const onDragUpdate = process.env.BUILD_ISEDM
        ? (update) => {
            var _a;
            const isEleDragging = update.source.droppableId === 'moreTab' && ((_a = update.destination) === null || _a === void 0 ? void 0 : _a.droppableId) === 'display';
            setIsEleInMoreTabDragging(isEleDragging);
        }
        : () => { };
    return (React.createElement("div", { id: "sirius-side-bar-root-container", className: classnames(style.sideBarTicky, inEdm ? style.sideBarTickyWaimao : style.sideBarTickyLingxi, inEdm ? 'waimao-sidebar' : '') },
        React.createElement("div", { id: "sirius-side-bar-root", className: classnames(style.sideBar, inEdm ? style.sideBarWaimao : '', inElectron() && 'webToolbarAvatarTagWrapper') },
            React.createElement("div", { style: { width: '100%', height: inElectron() && isMac ? ELECTRON_TITLE_FIX_HEIGHT : 0 } }),
            AvatarCompMemo,
            UpdateAppCompMemo,
            React.createElement(DragDropContext, { onDragEnd: onDragEnd, onDragUpdate: onDragUpdate, onDragStart: () => {
                    isDragging.current = true;
                } },
                React.createElement(Droppable, { droppableId: "display" }, provided => (React.createElement("div", Object.assign({ ref: ref => {
                        provided.innerRef(ref);
                        tabBarRef.current = ref;
                    } }, provided.droppableProps, { style: { flex: 1 } }),
                    TabToDisplayMemo,
                    !process.env.BUILD_ISEDM && CoopCompMemo,
                    TabInMoreCompMemo)))),
            AvatarListCompMemo),
        process.env.BUILD_ISEDM ? (React.createElement("div", { className: style.sideBarFooter },
            DownloadIconCompMemo,
            version === 'WEBSITE' && EnterpriseSettingMemo,
            version !== 'WEBSITE' && ToolSetMemo)) : null,
        process.env.BUILD_ISEDM && React.createElement(VideoDrawer, { getContainer: () => document.getElementById('sirius-side-bar-root-container') })));
};
export default SideTabBar;
